import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Button, LinearProgress,
    IconButton, TextField, InputAdornment,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    LockOutlined, AddOutlined, ArrowBackOutlined,
    CheckCircleOutline, RepeatOutlined, NorthEastOutlined,
    AutoGraphOutlined, ShowChartOutlined, PieChartOutlined,
    PauseCircleOutlined, PlayCircleOutlined,
} from '@mui/icons-material';
import { B, MUTUAL_FUNDS } from './investData';

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const ACTIVE_PLANS = [
    { id: 'ip1', name: 'Wealth Builder',    fundId: 'arm-discovery',      fundName: 'ARM Discovery Balanced', color: B.orange, freq: 'Monthly', amount: 20000, invested: 240000, currentVal: 291240, returns: 21.3, active: true,  nextDate: 'Jan 15' },
    { id: 'ip2', name: 'Dollar Hedge',      fundId: 'cowrywise-dollar',   fundName: 'Cowrywise Dollar Fund',  color: B.gold,   freq: 'Monthly', amount: 10000, invested: 80000,  currentVal: 87280,  returns: 9.1,  active: true,  nextDate: 'Jan 20' },
    { id: 'ip3', name: 'Equity Growth',     fundId: 'cardinalstone-equity',fundName: 'CardinalStone Equity',  color: B.purple, freq: 'Weekly',  amount: 5000,  invested: 65000,  currentVal: 90090,  returns: 38.6, active: false, nextDate: 'Paused' },
];

const FREQ_OPTIONS = [
    { key: 'daily',    label: 'Daily',      sub: 'Every day'          },
    { key: 'weekly',   label: 'Weekly',     sub: 'Every Monday'       },
    { key: 'monthly',  label: 'Monthly',    sub: '1st of every month' },
    { key: 'quarterly',label: 'Quarterly',  sub: 'Every 3 months'     },
];

const InvestmentPlansPage: React.FC = () => {
    const navigate = useNavigate();
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [view, setView]     = useState<'home'|'create'|'detail'>('home');
    const [sel, setSel]       = useState<typeof ACTIVE_PLANS[0]|null>(null);
    const [step, setStep]     = useState<'fund'|'config'|'confirm'|'success'>('fund');
    const [selFund, setSelFund] = useState('');
    const [planName, setPlanName] = useState('');
    const [amount, setAmount] = useState('');
    const [freq, setFreq]     = useState('monthly');

    const pageBg  = isDark ? B.deep : B.lavender;
    const surf    = isDark ? alpha(B.mid, .55) : '#fff';
    const bdr     = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const shd     = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri = isDark ? '#fff' : B.ink;
    const textMid = alpha(isDark ? '#fff' : B.ink, .45);
    const textFnt = alpha(isDark ? '#fff' : B.ink, .28);
    const divC    = alpha(isDark ? '#fff' : B.dark, .08);
    const glass   = { background: surf, border: bdr, boxShadow: shd, borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none' };

    const numAmt   = parseFloat(amount.replace(/,/g,'')) || 0;
    const selFundObj = MUTUAL_FUNDS.find(f => f.id === selFund);
    const totalInvested = ACTIVE_PLANS.reduce((s,p) => s + p.invested, 0);
    const totalVal      = ACTIVE_PLANS.reduce((s,p) => s + p.currentVal, 0);

    const Hero = ({ children, color = B.purple }: { children: React.ReactNode; color?: string }) => (
        <Box sx={{ background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${color} 100%)`, mt: { xs: '-76px', sm: 0 }, pt: { xs: '80px', sm: '28px' }, pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: -60, right: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.green, .12)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
            {children}
        </Box>
    );

    const BackBtn = ({ to }: { to: () => void }) => (
        <IconButton size="small" onClick={to} sx={{ color: alpha('#fff', .6), background: alpha('#fff', .08), '&:hover': { background: alpha('#fff', .16) } }}>
            <ArrowBackOutlined sx={{ fontSize: 18 }} />
        </IconButton>
    );

    const Wrap = ({ children }: { children: React.ReactNode }) => (
        <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
            {children}
        </Container>
    );

    /* ── DETAIL ── */
    if (view === 'detail' && sel) {
        const gain    = sel.currentVal - sel.invested;
        const gainPct = ((gain / sel.invested) * 100).toFixed(1);
        return (
            <Box sx={{ minHeight:'100vh', background:pageBg, pb:12, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
                <Hero color={sel.color}>
                    <Box display="flex" alignItems="center" gap={1.5} mb={3}><BackBtn to={() => setView('home')} /><Typography sx={{ color:'#fff', fontWeight:700 }}>{sel.name}</Typography></Box>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
                        <RepeatOutlined sx={{ color:alpha('#fff',.5), fontSize:16 }} />
                        <Typography sx={{ color:alpha('#fff',.5), fontSize:'.72rem' }}>{fmt(sel.amount)} {sel.freq}</Typography>
                    </Box>
                    <Typography sx={{ color:'#fff', fontFamily:"'Instrument Serif',serif", fontSize:'2rem', lineHeight:1 }}>{fmt(sel.currentVal)}</Typography>
                    <Box sx={{ display:'inline-flex', alignItems:'center', gap:.5, background:alpha(B.green,.15), border:`1px solid ${alpha(B.green,.25)}`, borderRadius:20, px:1.25, py:.4, mt:1 }}>
                        <NorthEastOutlined sx={{ color:B.green, fontSize:12 }} />
                        <Typography sx={{ color:B.green, fontSize:'.72rem', fontWeight:700 }}>+{gainPct}% · +{fmt(gain)}</Typography>
                    </Box>
                </Hero>
                <Wrap>
                    <Box sx={{ ...glass, p:2.5, mb:2 }}>
                        {([
                            ['Fund', sel.fundName],
                            ['Amount invested', fmt(sel.invested)],
                            ['Current value',   fmt(sel.currentVal)],
                            ['Gain',            `+${fmt(gain)} (+${gainPct}%)`],
                            ['Frequency',        sel.freq],
                            ['Amount per cycle', fmt(sel.amount)],
                            ['Next debit',       sel.nextDate],
                            ['Status',           sel.active ? 'Active' : 'Paused'],
                        ] as [string,string][]).map(([l,v],i,arr) => (
                            <Box key={l} sx={{ display:'flex', justifyContent:'space-between', py:1.1, borderBottom: i<arr.length-1 ? `1px solid ${divC}` : 'none' }}>
                                <Typography sx={{ color:textMid, fontSize:'.78rem' }}>{l}</Typography>
                                <Typography sx={{ color: l==='Gain' ? B.green : l==='Status' ? (sel.active ? B.green : B.orange) : textPri, fontWeight:600, fontSize:'.78rem' }}>{v}</Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box display="flex" gap={1.5}>
                        <Button fullWidth variant="contained" sx={{ background:`linear-gradient(135deg,${sel.color},${alpha(sel.color,.7)})`, borderRadius:'14px', textTransform:'none', fontWeight:700, py:1.5, boxShadow:'none' }}>
                            {sel.active ? 'Invest Now' : 'Resume Plan'}
                        </Button>
                        <Button fullWidth startIcon={sel.active ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                sx={{ border:`1px solid ${divC}`, color:textMid, borderRadius:'14px', textTransform:'none', fontWeight:700, py:1.5, '&:hover':{ background:alpha(B.mid,.06) } }}>
                            {sel.active ? 'Pause' : 'Resume'}
                        </Button>
                    </Box>
                </Wrap>
            </Box>
        );
    }

    /* ── CREATE ── */
    if (view === 'create') return (
        <Box sx={{ minHeight:'100vh', background:pageBg, pb:12, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
            <Hero>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <BackBtn to={() => step==='fund' ? setView('home') : setStep(step==='config'?'fund':step==='confirm'?'config':'fund')} />
                    <Typography sx={{ color:'#fff', fontWeight:700 }}>
                        {step==='fund'?'Choose a Fund':step==='config'?'Set Schedule':step==='confirm'?'Confirm Plan':'All Set!'}
                    </Typography>
                </Box>
            </Hero>
            <Wrap>
                {/* Step 1 — Pick fund */}
                {step==='fund' && <>
                    <Typography sx={{ color:textMid, fontSize:'.78rem', mb:2 }}>Pick the fund your recurring investment goes into.</Typography>
                    {MUTUAL_FUNDS.map(fund => {
                        const active = selFund === fund.id;
                        return (
                            <Box key={fund.id} onClick={() => setSelFund(fund.id)} sx={{ ...glass, p:2, mb:1.25, cursor:'pointer', border:`1px solid ${active ? alpha(fund.color,.4) : alpha(isDark?'#fff':B.dark,.07)}`, boxShadow: active ? `0 8px 24px ${alpha(fund.color,.18)}` : shd, transition:'all .18s' }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{ width:42, height:42, borderRadius:'12px', background:alpha(fund.color,.12), display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                        {fund.category==='Equity'?<ShowChartOutlined sx={{ color:fund.color, fontSize:18 }} />:fund.category==='Money Market'?<PieChartOutlined sx={{ color:fund.color, fontSize:18 }} />:<AutoGraphOutlined sx={{ color:fund.color, fontSize:18 }} />}
                                    </Box>
                                    <Box flex={1} minWidth={0}>
                                        <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.84rem' }} noWrap>{fund.name}</Typography>
                                        <Typography sx={{ color:textMid, fontSize:'.7rem' }}>{fund.manager}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign:'right', flexShrink:0 }}>
                                        <Typography sx={{ color:B.green, fontWeight:700, fontSize:'.84rem' }}>{fund.returns.yr1}%</Typography>
                                        <Typography sx={{ color:textFnt, fontSize:'.6rem' }}>1yr</Typography>
                                    </Box>
                                    {active && <CheckCircleOutline sx={{ color:fund.color, fontSize:18, flexShrink:0 }} />}
                                </Box>
                            </Box>
                        );
                    })}
                    <Button fullWidth variant="contained" disabled={!selFund} onClick={() => setStep('config')} sx={{ background:`linear-gradient(135deg,${B.purple},${B.light})`, borderRadius:'14px', textTransform:'none', fontWeight:700, py:1.6, boxShadow:'none', mt:1, '&:disabled':{ background:alpha(B.mid,.4), color:alpha('#fff',.3) } }}>Continue</Button>
                </>}

                {/* Step 2 — Config */}
                {step==='config' && <>
                    <Box sx={{ ...glass, p:2.5, mb:2 }}>
                        <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.88rem', mb:2 }}>Plan Details</Typography>

                        <Typography sx={{ color:textMid, fontSize:'.72rem', mb:1 }}>Plan name</Typography>
                        <TextField fullWidth placeholder="e.g. Wealth Builder" value={planName} variant="standard" onChange={e => setPlanName(e.target.value)}
                                   InputProps={{ sx:{ color:textPri, fontWeight:600, borderBottom:`2px solid ${planName ? B.purple : alpha(isDark?'#fff':B.dark,.15)}`, '&:before,&:after':{ display:'none' } } }} sx={{ mb:2.5 }} />

                        <Typography sx={{ color:textMid, fontSize:'.72rem', mb:1 }}>Amount per cycle</Typography>
                        <TextField fullWidth placeholder="0" value={amount} variant="standard"
                                   onChange={e => { const r = e.target.value.replace(/[^0-9]/g,''); setAmount(r ? parseInt(r).toLocaleString() : ''); }}
                                   InputProps={{ startAdornment:<InputAdornment position="start"><Typography sx={{ color:textMid, fontWeight:700 }}>₦</Typography></InputAdornment>, sx:{ color:textPri, fontWeight:700, fontSize:'1.1rem', borderBottom:`2px solid ${numAmt>0?B.purple:alpha(isDark?'#fff':B.dark,.15)}`, '&:before,&:after':{ display:'none' } } }}
                                   sx={{ mb:2.5 }} />
                        <Box sx={{ display:'flex', gap:1, mb:2.5 }}>
                            {[5000,10000,20000,50000].map(q => (
                                <Box key={q} onClick={() => setAmount(q.toLocaleString())} sx={{ flex:1, py:.7, borderRadius:20, cursor:'pointer', textAlign:'center', background: numAmt===q ? alpha(B.purple,.12) : alpha(isDark?'#fff':B.dark,.05), border:`1px solid ${numAmt===q ? alpha(B.purple,.3) : alpha(isDark?'#fff':B.dark,.1)}`, transition:'all .15s' }}>
                                    <Typography sx={{ color: numAmt===q ? B.purple : textMid, fontSize:'.72rem', fontWeight:700 }}>₦{q/1000}k</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Typography sx={{ color:textMid, fontSize:'.72rem', mb:1 }}>Frequency</Typography>
                        <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1 }}>
                            {FREQ_OPTIONS.map(f => (
                                <Box key={f.key} onClick={() => setFreq(f.key)} sx={{ p:1.25, borderRadius:'13px', cursor:'pointer', background: freq===f.key ? alpha(B.purple,.12) : alpha(isDark?'#fff':B.dark,.04), border:`1px solid ${freq===f.key ? alpha(B.purple,.3) : alpha(isDark?'#fff':B.dark,.08)}`, transition:'all .15s' }}>
                                    <Typography sx={{ color: freq===f.key ? B.purple : textPri, fontWeight:700, fontSize:'.8rem' }}>{f.label}</Typography>
                                    <Typography sx={{ color:textFnt, fontSize:'.66rem' }}>{f.sub}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Button fullWidth variant="contained" disabled={!planName || numAmt < 1000} onClick={() => setStep('confirm')} sx={{ background:`linear-gradient(135deg,${B.purple},${B.light})`, borderRadius:'14px', textTransform:'none', fontWeight:700, py:1.6, boxShadow:'none', '&:disabled':{ background:alpha(B.mid,.4), color:alpha('#fff',.3) } }}>Review Plan</Button>
                </>}

                {/* Step 3 — Confirm */}
                {step==='confirm' && selFundObj && <>
                    <Box sx={{ ...glass, p:2.5, mb:2 }}>
                        <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.88rem', mb:2 }}>{planName}</Typography>
                        {([
                            ['Fund',             selFundObj.name],
                            ['Amount per cycle', fmt(numAmt)],
                            ['Frequency',        FREQ_OPTIONS.find(f=>f.key===freq)?.label || freq],
                            ['Expected returns', `~${selFundObj.returns.yr1}% p.a.`],
                            ['Min investment',   fmt(selFundObj.minInvestment)],
                            ['Debit from',       'NG Credit Wallet'],
                        ] as [string,string][]).map(([l,v],i,arr) => (
                            <Box key={l} sx={{ display:'flex', justifyContent:'space-between', py:1, borderBottom:i<arr.length-1?`1px solid ${divC}`:'none' }}>
                                <Typography sx={{ color:textMid, fontSize:'.78rem' }}>{l}</Typography>
                                <Typography sx={{ color: l==='Expected returns'?B.green:textPri, fontWeight:600, fontSize:'.78rem' }}>{v}</Typography>
                            </Box>
                        ))}
                    </Box>
                    <Button fullWidth variant="contained" onClick={() => setStep('success')} sx={{ background:`linear-gradient(135deg,${B.purple},${B.light})`, borderRadius:'14px', textTransform:'none', fontWeight:700, py:1.6, boxShadow:'none', mb:1.5 }}>Activate Plan</Button>
                    <Button fullWidth onClick={() => setStep('config')} sx={{ color:textMid, textTransform:'none', fontWeight:600 }}>Go Back</Button>
                </>}

                {/* Step 4 — Success */}
                {step==='success' && (
                    <Box sx={{ textAlign:'center', pt:6 }}>
                        <Box sx={{ width:80, height:80, borderRadius:'50%', background:`linear-gradient(135deg,${B.purple},${B.green})`, display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:2.5 }}>
                            <RepeatOutlined sx={{ color:'#fff', fontSize:36 }} />
                        </Box>
                        <Typography sx={{ color:textPri, fontFamily:"'Instrument Serif',serif", fontSize:'1.6rem', mb:.75 }}>Plan Activated!</Typography>
                        <Typography sx={{ color:textMid, fontSize:'.85rem', mb:4, px:2 }}>{planName} will invest {fmt(numAmt)} {freq} starting now.</Typography>
                        <Button fullWidth variant="contained" onClick={() => { setView('home'); setStep('fund'); setPlanName(''); setAmount(''); setSelFund(''); setFreq('monthly'); }} sx={{ background:`linear-gradient(135deg,${B.purple},${B.light})`, borderRadius:'14px', textTransform:'none', fontWeight:700, py:1.5, boxShadow:'none' }}>Back to Plans</Button>
                    </Box>
                )}
            </Wrap>
        </Box>
    );

    /* ── HOME ── */
    const totalGain = totalVal - totalInvested;
    return (
        <Box sx={{ minHeight:'100vh', background:pageBg, pb:12, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
            <Hero>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <BackBtn to={() => navigate(-1)} />
                        <Box>
                            <Typography sx={{ color:alpha('#fff',.35), fontSize:'.62rem', letterSpacing:'.18em', textTransform:'uppercase' }}>NG Credit</Typography>
                            <Typography sx={{ color:'#fff', fontWeight:700, fontSize:'1.15rem', fontFamily:"'Instrument Serif',serif" }}>Investment Plans</Typography>
                        </Box>
                    </Box>
                    <Button onClick={() => { setView('create'); setStep('fund'); }} startIcon={<AddOutlined />} sx={{ background:alpha('#fff',.15), backdropFilter:'blur(8px)', border:`1px solid ${alpha('#fff',.2)}`, borderRadius:'11px', textTransform:'none', fontWeight:700, fontSize:'.78rem', color:'#fff', boxShadow:'none' }}>New Plan</Button>
                </Box>
            </Hero>
            <Wrap>
                {/* Summary */}
                <Box sx={{ ...glass, p:2.5, mb:2, border:`1px solid ${alpha(B.green,.18)}` }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box>
                            <Typography sx={{ color:textFnt, fontSize:'.6rem', letterSpacing:'.15em', textTransform:'uppercase', fontWeight:600, mb:.5 }}>Portfolio Value</Typography>
                            <Typography sx={{ color:textPri, fontFamily:"'Instrument Serif',serif", fontSize:'2.1rem', lineHeight:1 }}>{fmt(totalVal)}</Typography>
                        </Box>
                        <Box sx={{ textAlign:'right' }}>
                            <Box sx={{ display:'inline-flex', alignItems:'center', gap:.5, background:alpha(B.green,.12), border:`1px solid ${alpha(B.green,.22)}`, borderRadius:20, px:1.25, py:.4, mb:.5 }}>
                                <NorthEastOutlined sx={{ color:B.green, fontSize:12 }} />
                                <Typography sx={{ color:B.green, fontSize:'.72rem', fontWeight:700 }}>+{fmt(totalGain)}</Typography>
                            </Box>
                            <Typography sx={{ color:textFnt, fontSize:'.62rem', display:'block' }}>Total gains</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:1 }}>
                        {[['Plans',`${ACTIVE_PLANS.length}`],['Invested',fmt(totalInvested).replace('₦','₦').slice(0,8)],['Active',`${ACTIVE_PLANS.filter(p=>p.active).length}`]].map(([l,v]) => (
                            <Box key={l} sx={{ textAlign:'center', p:1.25, borderRadius:'12px', background:alpha(isDark?'#fff':B.dark,.04) }}>
                                <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.85rem' }}>{v}</Typography>
                                <Typography sx={{ color:textFnt, fontSize:'.62rem', mt:.2 }}>{l}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.9rem' }}>Active Plans</Typography>
                    <Typography sx={{ color:B.purple, fontSize:'.72rem', fontWeight:600 }}>{ACTIVE_PLANS.length} plans</Typography>
                </Box>

                {ACTIVE_PLANS.map(plan => {
                    const gain    = plan.currentVal - plan.invested;
                    const gainPct = ((gain / plan.invested) * 100).toFixed(1);
                    const progress = (plan.invested / (plan.invested + 100000)) * 100;
                    return (
                        <Box key={plan.id} onClick={() => { setSel(plan); setView('detail'); }} sx={{ ...glass, p:2.5, mb:1.5, cursor:'pointer', transition:'all .18s', '&:hover':{ transform:'translateY(-2px)', boxShadow:`0 12px 32px ${alpha(plan.color,.15)}` } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                <Box>
                                    <Box display="flex" alignItems="center" gap={.75} mb={.4}>
                                        <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.88rem' }}>{plan.name}</Typography>
                                        <Box sx={{ px:.75, py:.2, borderRadius:6, background: plan.active ? alpha(B.green,.1) : alpha(B.orange,.1), border:`1px solid ${plan.active ? alpha(B.green,.2) : alpha(B.orange,.2)}` }}>
                                            <Typography sx={{ color: plan.active ? B.green : B.orange, fontSize:'.58rem', fontWeight:700 }}>{plan.active ? 'ACTIVE' : 'PAUSED'}</Typography>
                                        </Box>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={.5}>
                                        <RepeatOutlined sx={{ color:textFnt, fontSize:13 }} />
                                        <Typography sx={{ color:textFnt, fontSize:'.7rem' }}>{fmt(plan.amount)} · {plan.freq}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ textAlign:'right' }}>
                                    <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.88rem' }}>{fmt(plan.currentVal)}</Typography>
                                    <Typography sx={{ color:B.green, fontSize:'.72rem', fontWeight:700 }}>+{gainPct}%</Typography>
                                </Box>
                            </Box>
                            <Box display="flex" justifyContent="space-between" mb={.75}>
                                <Typography sx={{ color:textFnt, fontSize:'.68rem' }}>Invested {fmt(plan.invested)}</Typography>
                                <Typography sx={{ color:textFnt, fontSize:'.68rem' }}>Next: {plan.nextDate}</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={progress} sx={{ height:5, borderRadius:4, background:alpha(plan.color,.1), '& .MuiLinearProgress-bar':{ background:`linear-gradient(90deg,${plan.color},${alpha(plan.color,.6)})`, borderRadius:4 } }} />
                        </Box>
                    );
                })}

                <Box sx={{ mt:3.5, display:'flex', alignItems:'center', justifyContent:'center', gap:.75 }}>
                    <LockOutlined sx={{ fontSize:11, color:alpha(isDark?'#fff':B.ink,.18) }} />
                    <Typography sx={{ fontSize:'.62rem', color:alpha(isDark?'#fff':B.ink,.18), letterSpacing:'.05em' }}>256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES</Typography>
                </Box>
            </Wrap>
        </Box>
    );
};

export default InvestmentPlansPage;